// src/pages/Enseignants/Events/EditResultCompetitorEvent.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { updateAdherentResults } from "../../../services/eventServices";
import {
  addPalmares,
  checkPalmaresExists,
  updatePalmares,
} from "../../../services/palmaresServices";
import { toast, ToastContainer } from "react-toastify";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes, FaTrophy, FaEdit } from "react-icons/fa";
import getCategory from "../../../utils/getCategory";
import getWeightCategory from "../../../utils/getWeightCategory";

const EditResultCompetitorEvent = ({
  isOpen,
  onClose,
  event,
  adherentDetails,
  results,
}) => {
  const [formData, setFormData] = useState({
    results: results?.resultats || "",
    ranking: results?.classement || "",
    observations: results?.observations || "",
    coach_hour: results?.coach_hour || 0,
  });

  const [palmaresExists, setPalmaresExists] = useState(false);
  const [palmaresId, setPalmaresId] = useState(null);
  const [saving, setSaving] = useState(false);

  // (Ré)hydrate quand on ouvre / change d’athlète ou d’événement
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      results: results?.resultats || "",
      ranking: results?.classement || "",
      observations: results?.observations || "",
      coach_hour: results?.coach_hour || 0,
    });
  }, [isOpen, results]);

  // Vérifie l’existence d’un palmarès pour cet adhérent / événement
  useEffect(() => {
    if (!isOpen || !adherentDetails?.id || !event?.id) return;
    (async () => {
      try {
        const r = await checkPalmaresExists(adherentDetails.id, event.id);
        if (r?.exists) {
          setPalmaresExists(true);
          setPalmaresId(r.palmaresId ?? null);
        } else {
          setPalmaresExists(false);
          setPalmaresId(null);
        }
      } catch {
        setPalmaresExists(false);
        setPalmaresId(null);
      }
    })();
  }, [isOpen, adherentDetails?.id, event?.id]);

  // Bloquer le scroll du fond
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const formatFR = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return Number.isNaN(dt.valueOf())
      ? d
      : dt.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  const getDefaultPhoto = (genre) =>
    genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

  // Fallback si pas fourni via props
  const ageCategory =
    adherentDetails?.ageCategory ||
    (adherentDetails?.date_naissance
      ? getCategory(adherentDetails.date_naissance)
      : "");
  const weightCategory =
    adherentDetails?.weightCategory ||
    (adherentDetails
      ? getWeightCategory(
          adherentDetails.genre,
          ageCategory,
          adherentDetails.poids
        )
      : "");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // On force coach_hour à rester numérique (mais on laisse vide si l’utilisateur efface)
    const v =
      name === "coach_hour" && value !== ""
        ? Number.isNaN(Number(value))
          ? formData.coach_hour
          : Number(value)
        : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleSaveResults = async () => {
    if (!adherentDetails?.id || !event?.id) {
      toast.error("Données incomplètes (adhérent ou événement).");
      return;
    }
    try {
      setSaving(true);
      const dataToSubmit = {
        adherent_id: adherentDetails.id,
        event_id: event.id,
        resultats: formData.results,
        classement: formData.ranking,
        observations: formData.observations,
        coach_hour: formData.coach_hour || 0,
      };
      await updateAdherentResults(dataToSubmit);
      toast.success("Résultats enregistrés avec succès.");
      onClose?.();
    } catch (error) {
      console.error("Failed to update results:", error);
      toast.error("Erreur lors de l'enregistrement des résultats.");
    } finally {
      setSaving(false);
    }
  };

  const handlePalmaresAction = async () => {
    if (!adherentDetails?.id || !event?.id) {
      toast.error("Données incomplètes (adhérent ou événement).");
      return;
    }

    const palmaresData = {
      adherent_palmares_id: adherentDetails.id,
      event_palmares_id: event.id,
      date_palmares: new Date().toISOString(),
      titre_palmares: event.titre,
      resultats_palmares: formData.results,
      classement_palmares: formData.ranking,
      observations_palmares: formData.observations,
    };

    try {
      if (palmaresExists) {
        if (!palmaresId) {
          toast.error("Impossible de mettre à jour : ID palmarès manquant.");
          return;
        }
        await updatePalmares(palmaresId, palmaresData);
        toast.success("Palmarès mis à jour avec succès.");
      } else {
        await addPalmares(palmaresData);
        toast.success("Ajouté au Palmarès avec succès.");
      }
      onClose?.();
    } catch (error) {
      console.error("Erreur palmarès:", error);
      toast.error("Erreur lors de l'ajout/mise à jour du palmarès.");
    }
  };

  const addResultTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      results: `${prev.results || ""}${tag} | `,
    }));
  };

  const removeLastTag = () => {
    const parts = (formData.results || "").split(" | ");
    if (parts.length <= 1) return;
    const trimmed = parts.slice(0, -2).join(" | "); // -2 pour enlever le tag et la chaîne vide finale
    setFormData((prev) => ({
      ...prev,
      results: trimmed ? `${trimmed} | ` : "",
    }));
  };

  const isFormFilled =
    !!formData.results || !!formData.ranking || !!formData.observations;

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (!isOpen) return null;

  // ---------- Rendu plein écran (portal) ----------
  return createPortal(
    <div className="fixed inset-0 z-[1000]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Panel plein écran */}
      <div
        className="absolute inset-0 flex items-stretch justify-center"
        onClick={onClose}
      >
        <div
          className="relative w-screen h-screen overflow-y-auto bg-white/90 dark:bg-slate-900/80"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header dégradé sticky */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
            <div className="px-5 sm:px-7 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold truncate">
                  {event?.titre || "Résultats – Compétition"}
                </h2>
                {event?.start && (
                  <div className="text-xs opacity-90 mt-0.5">
                    {formatFR(event.start)}
                  </div>
                )}
              </div>
              <Button
                text="Fermer"
                icon={FaTimes}
                onClick={onClose}
                className="!rounded-xl bg-white/10 hover:bg-white/20 text-white gap-2"
              />
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 py-6 sm:px-8">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Colonne gauche : fiche athlète */}
              <section className="lg:col-span-1">
                <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 p-4">
                  <div className="flex items-center gap-3">
                    <img
                      className="h-16 w-16 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      src={
                        toAbsolute(adherentDetails?.photo_url) ||
                        getDefaultPhoto(adherentDetails?.genre)
                      }
                      alt="Adhérent"
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {(adherentDetails?.nom || "").toUpperCase()}{" "}
                        {adherentDetails?.prenom || ""}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        {ageCategory || "—"} {weightCategory ? `• ${weightCategory}` : ""}
                      </div>
                    </div>
                  </div>

                  {/* Heures de coaching (coach) */}
                  {adherentDetails?.isCoach && (
                    <div className="mt-4">
                      <label className="block text-xs mb-1 opacity-70">
                        Heures de coaching
                      </label>
                      <input
                        type="number"
                        name="coach_hour"
                        value={formData.coach_hour}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  )}
                </div>
              </section>

              {/* Colonne droite : formulaires */}
              <section className="lg:col-span-2 space-y-6">
                {/* Tags rapides + Résultats */}
                <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs opacity-70">Raccourcis :</span>
                    <button
                      onClick={() => addResultTag("✅")}
                      className="px-3 py-1 rounded-full bg-emerald-500 text-white text-sm hover:opacity-90"
                      title="Victoire"
                      type="button"
                    >
                      Gagné
                    </button>
                    <button
                      onClick={() => addResultTag("❌")}
                      className="px-3 py-1 rounded-full bg-rose-500 text-white text-sm hover:opacity-90"
                      title="Défaite"
                      type="button"
                    >
                      Perdu
                    </button>
                    <button
                      onClick={() => addResultTag("N")}
                      className="px-3 py-1 rounded-full bg-amber-500 text-white text-sm hover:opacity-90"
                      title="Nul"
                      type="button"
                    >
                      Nul
                    </button>
                    <button
                      onClick={() => addResultTag("F")}
                      className="px-3 py-1 rounded-full bg-slate-500 text-white text-sm hover:opacity-90"
                      title="Forfait"
                      type="button"
                    >
                      Forfait
                    </button>
                    <button
                      onClick={removeLastTag}
                      className="px-3 py-1 rounded-full bg-indigo-500 text-white text-sm hover:opacity-90"
                      type="button"
                      title="Effacer le dernier tag"
                    >
                      ⌫
                    </button>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs mb-1 opacity-70">
                      Résultats (ex : ✅ | ❌ | …)
                    </label>
                    <input
                      type="text"
                      name="results"
                      value={formData.results}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                      placeholder="Saisissez ou utilisez les raccourcis"
                    />
                  </div>
                </div>

                {/* Classement + Observations */}
                <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 p-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs mb-1 opacity-70">
                        Classement
                      </label>
                      <input
                        type="text"
                        name="ranking"
                        value={formData.ranking}
                        onChange={handleInputChange}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                        placeholder="ex : 1er, 2e, 3e…"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs mb-1 opacity-70">
                        Observations
                      </label>
                      <textarea
                        name="observations"
                        value={formData.observations}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                        placeholder="Notes d’analyse, axes d’amélioration…"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* Footer sticky (actions) */}
          <div className="sticky bottom-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-t border-slate-200 dark:border-slate-800">
            <div className="px-5 sm:px-8 py-4 flex flex-wrap items-center justify-center gap-2">
              <Button
                text={saving ? "Enregistrement…" : "Sauvegarder"}
                onClick={handleSaveResults}
                icon={FaSave}
                className="!rounded-xl gap-2"
              />
              {isFormFilled && (
                <Button
                  text={palmaresExists ? "Màj du Palmarès" : "Ajouter au Palmarès"}
                  onClick={handlePalmaresAction}
                  icon={palmaresExists ? FaEdit : FaTrophy}
                  className="!rounded-xl gap-2"
                />
              )}
              <Button
                text="Annuler"
                onClick={onClose}
                icon={FaTimes}
                className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditResultCompetitorEvent;
